import { useGame } from '@/app/providers/GameContextProvider';
import React, { useRef, useState } from 'react'
import { GameMode } from '@/lib/types/common/GameMode';
import { useTranslation } from '@/hooks/useTranslation';
import TooltipFFXIV from '@/app/components/TooltipFFXIV';
import { getGameModeByType } from '@/lib/types/common/GameModeAdvancedSettings';
import { useGlobalPopup } from '@/app/providers/GlobalPopupProvider';
import Image from 'next/image';
import { useAudio, AUDIO } from '@/app/providers/AudioProvider';
import { isTodayDailyCompleted } from '@/lib/utils/dailyUtils';

const GameModeButton = ({
    gameMode,
}: {
    gameMode: GameMode;
}) => {
    const { room, gameState } = useGame();
    const { showPopup } = useGlobalPopup();
    const [pressedModeId, setPressedModeId] = useState<GameMode | null>(null);
    const [circleAnimModeId, setCircleAnimModeId] = useState<GameMode | null>(null);
    const [circleAnimKey, setCircleAnimKey] = useState<number>(0);
    const { t } = useTranslation();
    const { play } = useAudio();
    const circleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isSelected = gameState?.gameMode === gameMode;
    const isPressed = pressedModeId === gameMode;
    const showCircle = circleAnimModeId === gameMode;
    const gameModeConfig = getGameModeByType(gameMode);

    if (!room || !gameState) return null;

    const isGameMaster = room.sessionId === gameState.gameMasterId;

    // Handle game mode selection and send to room
    const handleGameModeSelect = (selectedGameMode: GameMode) => {
        if (!room || !gameState) return;

        if (!isGameMaster) {
            // Only allow game master to change mode when in lobby
            showPopup({
                type: 'error',
                message: t('lobby.onlyGameMasterCan'),
            });
            return;
        }

        play(AUDIO.CLICK);

        // Save selected game mode to localStorage for persistence on reload
        if (typeof window !== 'undefined') {
            localStorage.setItem('eorguessr_selected_gamemode', selectedGameMode);
        }

        room.send("changeGameMode", { gameMode: selectedGameMode });
    };

    return (
        <div className="relative flex flex-col gap-1 items-center group" key={gameMode}>
            <TooltipFFXIV showInMobile={false}>{t(`game.modes.${gameMode}.description`)}</TooltipFFXIV>
            <div
                className={`relative w-16 h-16 group  ${gameModeConfig?.status === 'available' && isGameMaster ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'} ${gameModeConfig?.status === 'available' ? '' : 'grayscale'}`}
                key={gameMode}
                onMouseEnter={() => gameModeConfig?.status === 'available' && isGameMaster && play(AUDIO.HOVER)}
                onClick={() => gameModeConfig?.status === 'available' && handleGameModeSelect(gameMode)}
                onMouseDown={() => {
                    setPressedModeId(gameMode);
                    setCircleAnimModeId(gameMode);
                    setCircleAnimKey(circleAnimKey + 1);
                    if (circleTimeoutRef.current) {
                        clearTimeout(circleTimeoutRef.current);
                    }
                    circleTimeoutRef.current = setTimeout(() => {
                        setCircleAnimModeId(null);
                    }, 200);
                }}
                onMouseUp={() => setPressedModeId(null)}
                onMouseLeave={() => {
                    setPressedModeId(null);
                }}
            >
                {gameMode === GameMode.DAILY && !isTodayDailyCompleted() && (
                    <div className="absolute top-1/2 left-1/2 translate-x-[-50%] translate-y-[-50%] flex items-center justify-center w-56 h-56 z-[0]">
                        <img
                            src="highlight_marker.png"
                            alt="Marker Highlight"
                            width={100}
                            height={100}
                            className="animate-[spin_2.5s_linear_infinite]"
                        />
                    </div>
                )}

                <div className="bg-[#323232] absolute w-full h-full rounded-xl"></div>

                {/* Top highlight */}
                <div className={`absolute top-0 left-0 w-full h-full rounded-xl pointer-events-none z-20 border-t-[3px] border-[#EFE9E0]/70 ${!isSelected ? 'opacity-50' : ''}`}></div>
                {/* Glow: pressed shows selected glow, otherwise hover or selected */}
                {gameModeConfig?.status === 'available' && (
                    <div
                        className={
                            `absolute top-[-2px] left-[-2px] w-[67px] h-[67px] rounded-xl pointer-events-none z-40 duration-100 group-hover:bg-[#FFF6CA]/20
									${isPressed
                                ? 'opacity-100 border-[4px] border-[#FFF6CA] shadow-[0px_0px_15px_#FFF6CA,inset_0px_0px_10px_#FFF6CA]'
                                : isSelected
                                    ? 'opacity-100 border-[4px] border-[#FFF6CA] shadow-[0px_0px_10px_#FFF6CA,inset_0px_0px_10px_#FFF6CA] group-hover:opacity-100 group-hover:bg-[#FFF6CA]/20 group-hover:shadow-[0px_0px_10px_#FFF6CA,inset_0px_0px_20px_#FFF6CA] group-hover:border-[4px] group-hover:border-[#FFF6CA]'
                                    : 'opacity-0 group-hover:opacity-100 group-hover:shadow-[0px_0px_10px_#FFF6CA,inset_0px_0px_20px_#FFF6CA] group-hover:border-[4px] group-hover:border-[#FFF6CA]'
                            }
									`
                        }
                    ></div>
                )}
                <div
                    className={`relative w-16 h-16 rounded-xl flex items-center justify-center overflow-hidden shadow-black/50 z-10 shadow-md ${!isSelected ? 'opacity-50' : ''}`}
                >
                    {/* Growing white circle animation on press */}
                    {showCircle && gameModeConfig?.status === 'available' && (
                        <div
                            key={circleAnimKey}
                            className="absolute left-1/2 top-1/2 z-50 pointer-events-none"
                            style={{
                                transform: 'translate(-50%, -50%) scale(0)',
                                animation: 'growCircle 0.2s cubic-bezier(0.4,0,0.2,1) forwards',
                                width: '60px',
                                height: '60px',
                                borderRadius: '9999px',
                                background: 'white',
                                opacity: 0.7,
                                overflow: 'hidden',
                            }}
                        />
                    )}
                    {/* White top glow */}
                    <div className="top-0 -translate-y-1/2 absolute w-[140%] h-[40px] bg-gradient-to-b from-white to-transparent rounded-[60%]"></div>
                    <Image
                        src={`/game_modes/${gameMode}.png`}
                        width={64}
                        height={64}
                        alt={t(`game.modes.${gameMode}.name`)}
                    />
                </div>
                {/* Name */}
                <div className="absolute translate-y-full w-full bottom-[10px] text-center uppercase text-white user-select-none select-none z-[100]">
                    <div className="absolute left-0 text-center w-full text-shadow shadow-black">{t(`game.modes.${gameMode}.name`)}</div>
                    <div className="absolute left-0 text-center w-full text-shadow-border shadow-yellow-700">{t(`game.modes.${gameMode}.name`)}</div>
                </div>
            </div>

            <style>
                {`
@keyframes growCircle {
	0% {
		transform: translate(-50%, -50%) scale(0.2);
		opacity: 0.7;
	}
	80% {
		opacity: 0.3;
	}
	100% {
		transform: translate(-50%, -50%) scale(1.2);
		opacity: 0;
	}
}
`}
            </style>
        </div>
    )
}

export default GameModeButton